import {
  Box,
  Burger,
  Button,
  Divider,
  Drawer,
  Flex,
  Group,
  List,
  Menu,
  Paper,
  Stack,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCoins, IconDoorExit, IconHome, IconUserCircle, IconWallet } from "@tabler/icons-react";
import { AccessChecker } from "components/auth/AccessChecker";
import { Avatar } from "components/_common/Avatar";
import { PageContainer } from "components/_common/_page/PageContainer";
import React, { useRef } from "react";
import { Link as RouteLink, useLocation } from "react-router-dom";
import { UserRole } from "shared/const/userRole";
import { useLogout } from "shared/hooks/useLogout";
import { useMobile } from "shared/hooks/useMobile";
import BootstrapGrid from "shared/ui/BootstrapGrid";
import { Link } from "shared/ui/_links/Link";
import { MainLogo } from "shared/ui/_logos/MainLogo";
import styles from "./index.module.scss";

const MENU = [
  { label: "Проекты", to: "/projects" },
  // { label: "Калькулятор", to: "/landing" },
  { label: "Документы", to: "/platform-docs" },
  { label: "Новости и статьи", to: "/news" },
];

const AUTH_MENU = [
  { label: "Личный кабинет", to: "/main", Icon: IconHome },
  { label: "Счета", to: "/accounts", Icon: IconWallet },
  { label: "Сделки", to: "/deals", Icon: IconCoins },
  { label: "Настройки профиля", to: "/profile", Icon: IconUserCircle },
];

const Z_INDEX = 199;

export const Header = () => {
  const { isMobile } = useMobile();

  const [openedMobileMenu, { open: openMobileMenu, close: closeMobileMenu }] = useDisclosure();
  const paddings = ["30px 16px", "20px 0"];
  const location = useLocation();
  const logout = useLogout();
  const [isWindowScrolled, setIsWindowScrolled] = React.useState(false);
  const theme = useMantineTheme();
  const topBarRef = useRef<null | HTMLDivElement>(null);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      if (scrollTop > 0) {
        setIsWindowScrolled(true);
      } else {
        setIsWindowScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const isActiveLink = (to: string, contains: boolean = true) => {
    if (!contains) {
      return location.pathname === to;
    }

    return location.pathname.includes(to);
  };

  React.useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    if (!openedMobileMenu) {
      if (topBarRef.current && window.scrollY < topBarRef.current.offsetHeight) {
        window.scroll({ top: topBarRef.current.offsetHeight, behavior: "smooth" });
      }
      openMobileMenu();
      return;
    }
    closeMobileMenu();
  };

  if (isMobile)
    return (
      <Box component={"header"} pos={"sticky"} top={0} style={{ zIndex: Z_INDEX }} pt={20}>
        <BootstrapGrid.Row>
          <BootstrapGrid.Item
            p={10}
            className={styles.paper}
            bg={"white"}
            component={Paper}
            align={"center"}
            justify={"space-between"}
            without={"Pb"}
          >
            <Group align={"center"} justify={"space-between"}>
              <MainLogo width={100} />
              <Burger
                mr={10}
                opened={openedMobileMenu}
                onClick={toggleMobileMenu}
                aria-label="Toggle navigation"
              />
              <Drawer
                bg={"gray.1"}
                position="left"
                title={
                  <Box p={10}>
                    <MainLogo width={100} />
                  </Box>
                }
                opened={openedMobileMenu}
                onClose={closeMobileMenu}
              >
                <Stack>
                  <Divider />
                  <List component={Stack}>
                    <Link
                      fw={500}
                      variant={"transparent"}
                      c={isActiveLink("/", false) ? "violet" : "black"}
                      size={"xl"}
                      p={0}
                      to={"/"}
                    >
                      Главная
                    </Link>
                    {MENU.map(({ label, to }, index) => (
                      <Link
                        fw={500}
                        variant={"transparent"}
                        c={isActiveLink(to) ? "violet" : "black"}
                        size={"xl"}
                        p={0}
                        key={index}
                        to={to}
                      >
                        {label}
                      </Link>
                    ))}
                  </List>
                  <Divider />
                  <AccessChecker isAuthenticated={true} role={[UserRole.investor]}>
                    {AUTH_MENU.map(({ label, to }, index) => (
                      <Link
                        fw={500}
                        variant={"transparent"}
                        c={isActiveLink(to) ? "primary" : "black"}
                        size={"xl"}
                        p={0}
                        key={index}
                        to={to}
                      >
                        {label}
                      </Link>
                    ))}
                  </AccessChecker>
                  <AccessChecker>
                    <Divider />
                    <Button variant={"default"} leftSection={<IconDoorExit />} onClick={logout}>
                      Выйти
                    </Button>
                  </AccessChecker>
                  <AccessChecker isAuthenticated={false}>
                    <Group gap={10} justify={"left"}>
                      <Button component={RouteLink} to={"/auth/sign-in"} size={"lg"}>
                        Вход
                      </Button>
                      <Button component={RouteLink} to={"/auth/sign-up"} size={"lg"}>
                        Регистрация
                      </Button>
                    </Group>
                  </AccessChecker>
                </Stack>
              </Drawer>
            </Group>
          </BootstrapGrid.Item>
        </BootstrapGrid.Row>
      </Box>
    );

  return (
    <PageContainer
      component={"header"}
      pos={"sticky"}
      size={"xl"}
      top={0}
      pt={30}
      pb={30}
      style={{ zIndex: 1000 }}
    >
      <Paper bg={"white"} h={50} className={styles.paper} shadow={"md"} p={0}>
        <Flex align={"center"} justify={"space-between"}>
          <Group gap={15} align={"center"}>
            <MainLogo width={120} />
            <Group gap={10}>
              {MENU.map(({ label, to }, index) => (
                <Link c={"black"} key={index} to={to} style={{ cursor: "pointer" }}>
                  {label}
                </Link>
              ))}
            </Group>
          </Group>

          <AccessChecker isAuthenticated={true}>
            <Flex justify={"end"} align={"center"}>
              <Button component={RouteLink} to={"/deals/create"} leftSection={<IconCoins />}>
                Инвестировать
              </Button>
              <Menu>
                <Menu.Target>
                  <Box>
                    <Avatar component={"button"} h={50} w={50} />
                  </Box>
                </Menu.Target>
                <Menu.Dropdown>
                  {AUTH_MENU.map(({ label, to, Icon }, index) => (
                    <Box key={index} mb={8}>
                      <Link to={to} style={{ textDecoration: "none" }}>
                        <Menu.Item leftSection={<Icon />} color={"black"} key={index}>
                          {label}
                        </Menu.Item>
                      </Link>
                    </Box>
                  ))}
                  <Menu.Item onClick={logout} leftSection={<IconDoorExit />}>
                    Выйти
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Flex>
          </AccessChecker>
          <AccessChecker isAuthenticated={false}>
            <Group>
              <Button component={RouteLink} to={"/auth/sign-in"} variant={"subtle"} c={"black"}>
                Вход
              </Button>
              <Button component={RouteLink} to={"/auth/sign-up"} h={50} bg={"violet.3"} c={"white"}>
                Регистрация
              </Button>
            </Group>
          </AccessChecker>
        </Flex>
      </Paper>
    </PageContainer>
  );
};
